import type {Scene} from '@babylonjs/core/scene';
import {Mesh} from '@babylonjs/core/Meshes/mesh';
import {VertexData} from '@babylonjs/core/Meshes/mesh.vertexData';
import {StandardMaterial} from '@babylonjs/core/Materials/standardMaterial';
import {DynamicTexture} from '@babylonjs/core/Materials/Textures/dynamicTexture';
import {Texture} from '@babylonjs/core/Materials/Textures/texture';
import {Color3} from '@babylonjs/core/Maths/math.color';
import {TERRAIN,terrainCells,terrainOuterFloors,type ArenaDefinition,type TerrainDamage} from '@arena/shared';
import {surfaceTexture,surfaceRepeat} from './pixelTextures.ts';

/** Three shared draw calls: floor, exposed rock faces and progressive cracks. */
export class InfernoTerrainView {
  readonly meshes:Mesh[];
  private readonly top:Mesh;
  private readonly walls:Mesh;
  private readonly cracks:Mesh;
  private readonly arena:ArenaDefinition;
  private key:string|null=null;
  constructor(scene:Scene,arena:ArenaDefinition){
    this.arena=arena;
    this.top=new Mesh('inferno-destructible-floor',scene);
    this.walls=new Mesh('inferno-excavation-walls',scene);
    this.cracks=new Mesh('inferno-floor-cracks',scene);
    this.meshes=[this.top,this.walls,this.cracks];
    for(const m of this.meshes){m.isPickable=false;m.receiveShadows=true;}
    const floor=new StandardMaterial('inferno-terrain-ash',scene);
    floor.diffuseTexture=surfaceTexture(scene,'ash');
    (floor.diffuseTexture as Texture).uScale=(floor.diffuseTexture as Texture).vScale=surfaceRepeat('ash');
    floor.specularColor=Color3.Black();floor.maxSimultaneousLights=8;this.top.material=floor;
    const rock=new StandardMaterial('inferno-terrain-rock',scene);
    rock.diffuseTexture=new Texture('/proto2d/inferno-v2/cliff.png',scene,true,true,Texture.NEAREST_SAMPLINGMODE);
    rock.specularColor=Color3.Black();rock.backFaceCulling=false;rock.twoSidedLighting=true;rock.maxSimultaneousLights=8;this.walls.material=rock;
    const atlas=new DynamicTexture('terrain-crack-atlas',{width:256,height:64},scene,false,Texture.NEAREST_SAMPLINGMODE);
    const g=atlas.getContext() as unknown as CanvasRenderingContext2D;
    g.clearRect(0,0,256,64);
    for(let stage=1;stage<=3;stage++){
      g.save();g.beginPath();g.rect(stage*64,0,64,64);g.clip();
      for(let i=0;i<stage*2+1;i++){
        const angle=i*2.399+.35,length=12+(i*7)%18,cx=stage*64+31,cy=33;
        g.strokeStyle='#291e19';g.lineWidth=stage===3?1.6:1.2;g.beginPath();g.moveTo(cx,cy);
        for(let j=1;j<=4;j++){
          const r=length*j/4,a=angle+Math.sin(i*3+j)*.16;
          g.lineTo(cx+Math.cos(a)*r,cy+Math.sin(a)*r);
        }
        g.stroke();
      }
      g.restore();
    }
    atlas.hasAlpha=true;atlas.update();
    const scars=new StandardMaterial('terrain-scars',scene);scars.diffuseTexture=atlas;scars.useAlphaFromDiffuseTexture=true;
    scars.disableLighting=true;scars.emissiveColor=Color3.White();scars.backFaceCulling=false;this.cracks.material=scars;
    this.update([]);
  }
  update(states:Iterable<[string,TerrainDamage]>):void {
    const values=new Map(states),key=[...values].map(([id,s])=>`${id}:${s.depth}:${s.hp}`).join('|');
    if(key===this.key)return;this.key=key;
    const tops=new Geometry(),faces=new Geometry(),scars=new Geometry();
    const surface=(x0:number,x1:number,z0:number,z1:number,y:number)=>tops.quad([x0,y,z0,x1,y,z0,x1,y,z1,x0,y,z1],[x0/4,z0/4,x1/4,z0/4,x1/4,z1/4,x0/4,z1/4]);
    for(const b of terrainOuterFloors(this.arena)){
      // The pier is drawn separately in timber.
      if(b.id==='pier')continue;
      surface(b.min.x,b.max.x,b.min.z,b.max.z,b.max.y+.002);
    }
    for(const cell of terrainCells(this.arena)){
      const state=values.get(cell.id),depth=state?.depth??0,y=-depth*TERRAIN.layer;
      const x0=cell.x-1,x1=cell.x+1,z0=cell.z-1,z1=cell.z+1;
      surface(x0,x1,z0,z1,y+.002);
      const stage=!state || state.depth===TERRAIN.maxDepth?0:state.hp===TERRAIN.hp?0:Math.min(3,1+Math.floor((TERRAIN.hp-state.hp-1)/10));
      scars.quad([x0,y+.012,z0,x1,y+.012,z0,x1,y+.012,z1,x0,y+.012,z1],[stage/4,0,(stage+1)/4,0,(stage+1)/4,1,stage/4,1]);
      const corners=[[x0,z0],[x1,z0],[x1,z1],[x0,z1]];
      for(let i=0;i<4;i++){
        const [ax,az]=corners[i]!,[bx,bz]=corners[(i+1)%4]!;
        const bottom=-(TERRAIN.maxDepth+1)*TERRAIN.layer;
        faces.quad([ax!,bottom,az!,bx!,bottom,bz!,bx!,y,bz!,ax!,y,az!],[0,0,1,0,1,(y-bottom)/2,0,(y-bottom)/2]);
      }
    }
    tops.apply(this.top);faces.apply(this.walls);scars.apply(this.cracks);
  }
}

class Geometry {
  private positions:number[]=[];private uvs:number[]=[];private indices:number[]=[];
  quad(p:number[],uv:number[]):void {
    const n=this.positions.length/3;this.positions.push(...p);this.uvs.push(...uv);
    // Babylon's default scene is left-handed: this winding faces upward.
    this.indices.push(n,n+1,n+2,n,n+2,n+3);
  }
  apply(mesh:Mesh):void {
    const data=new VertexData(),normals:number[]=[];
    VertexData.ComputeNormals(this.positions,this.indices,normals);
    data.positions=this.positions;data.normals=normals;data.uvs=this.uvs;data.indices=this.indices;data.applyToMesh(mesh,true);
  }
}
